/*!
 * QC UTM Attribution — utm-capture.js
 * ═══════════════════════════════════════════════════════════════════
 *
 * FIELD MAP — every name this script writes into Shopify contact forms.
 * Use these keys 1:1 in your Zapier "Catch Hook" step.
 *
 *  LAST-TOUCH (overwritten on every tagged landing; cookie lt_*, ls lt_*)
 *  ─────────────────────────────────────────────────────────────────────
 *  contact[utm_source]        contact[utm_medium]        contact[utm_campaign]
 *  contact[utm_term]          contact[utm_content]
 *  contact[campaign_id]       contact[adset_id]          contact[ad_id]
 *  contact[ad_name]           contact[campaign_name]
 *  contact[gclid]             contact[gbraid]            contact[wbraid]
 *  contact[fbclid]
 *  contact[landing_page]      contact[referrer]
 *
 *  FIRST-TOUCH (written once, never overwritten; cookie ft_*, ls ft_*)
 *  ─────────────────────────────────────────────────────────────────────
 *  contact[first_utm_source]     contact[first_utm_medium]     contact[first_utm_campaign]
 *  contact[first_utm_term]       contact[first_utm_content]
 *  contact[first_campaign_id]    contact[first_adset_id]       contact[first_ad_id]
 *  contact[first_ad_name]        contact[first_campaign_name]
 *  contact[first_gclid]          contact[first_gbraid]         contact[first_wbraid]
 *  contact[first_fbclid]
 *  contact[first_landing_page]   contact[first_referrer]
 *
 * ═══════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  /* ── Constants ─────────────────────────────────────────────────── */
  var TRACKING_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'campaign_id', 'adset_id', 'ad_id', 'ad_name', 'campaign_name',
    'gclid', 'gbraid', 'wbraid', 'fbclid'
  ];

  var LT_DAYS  = 90;
  var FT_DAYS  = 365;

  /* Backward-compat key shared with qc-quote-scripts.liquid inline scripts. */
  var SS_KEY = 'qc_attribution_v1';

  /* ── Cookie helpers ─────────────────────────────────────────────── */
  function setCookie(name, value, days) {
    try {
      var exp = new Date(Date.now() + days * 864e5).toUTCString();
      document.cookie = name + '=' + encodeURIComponent(value) +
        '; expires=' + exp + '; path=/; SameSite=Lax';
    } catch (e) {}
  }

  function getCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }

  /* ── localStorage helpers ───────────────────────────────────────── */
  function lsSet(key, value) {
    try { localStorage.setItem(key, value); } catch (e) {}
  }

  function lsGet(key) {
    try { return localStorage.getItem(key) || ''; } catch (e) { return ''; }
  }

  /* ── sessionStorage compat (qc-quote-scripts.liquid reads this) ─── */
  function ssLoad() {
    try { return JSON.parse(sessionStorage.getItem(SS_KEY) || '{}'); } catch (e) { return {}; }
  }

  function ssSave(obj) {
    try { sessionStorage.setItem(SS_KEY, JSON.stringify(obj)); } catch (e) {}
  }

  /* ── Storage read ───────────────────────────────────────────────── */
  function getStored(key, touch) {
    var prefix = (touch === 'ft') ? 'ft_' : 'lt_';
    return getCookie(prefix + key) || lsGet(prefix + key) || '';
  }

  /* ── Storage write ──────────────────────────────────────────────── */
  function writeLt(key, value) {
    setCookie('lt_' + key, value, LT_DAYS);
    lsSet('lt_' + key, value);
  }

  function writeFt(key, value) {
    if (getCookie('ft_' + key) || lsGet('ft_' + key)) return; /* never overwrite */
    setCookie('ft_' + key, value, FT_DAYS);
    lsSet('ft_' + key, value);
  }

  /* ── Parse current URL params ───────────────────────────────────── */
  var urlParams;
  try { urlParams = new URLSearchParams(window.location.search); }
  catch (e) { urlParams = { get: function () { return null; }, has: function () { return false; } }; }

  var captured = {};
  TRACKING_KEYS.forEach(function (key) {
    var v = urlParams.get(key);
    if (v) captured[key] = v;
  });

  var hasCampaign = Object.keys(captured).length > 0;

  /* ── Persist ────────────────────────────────────────────────────── */
  if (hasCampaign) {
    /* Last-touch: overwrite every key that is present in this URL. */
    TRACKING_KEYS.forEach(function (key) {
      if (captured[key]) writeLt(key, captured[key]);
    });

    /* First-touch: write each present key only if never stored before. */
    TRACKING_KEYS.forEach(function (key) {
      if (captured[key]) writeFt(key, captured[key]);
    });

    /* landing_page and referrer are always first-touch only. */
    writeFt('landing_page', window.location.pathname);
    writeFt('referrer', document.referrer || '');

    /* Also write last-touch for landing_page / referrer (overwrite each visit). */
    writeLt('landing_page', window.location.pathname);
    writeLt('referrer', document.referrer || '');

    /* ── sessionStorage shim ─────────────────────────────────────── */
    /* qc-quote-scripts.liquid reads from sessionStorage on DOMContentLoaded.
       We merge our values in so the inline script keeps working even after
       we strip the visible URL params below. */
    var ss = ssLoad();
    TRACKING_KEYS.forEach(function (key) {
      if (captured[key]) ss[key] = captured[key];
    });
    ssSave(ss);

    /* ── Strip tracking params from the address bar ──────────────── */
    try {
      var url = new URL(window.location.href);
      var changed = false;
      TRACKING_KEYS.forEach(function (key) {
        if (url.searchParams.has(key)) {
          url.searchParams.delete(key);
          changed = true;
        }
      });
      if (changed) {
        var qs = url.searchParams.toString();
        var clean = url.pathname + (qs ? '?' + qs : '') + url.hash;
        history.replaceState(null, '', clean);
      }
    } catch (e) {}
  }

  /* ── Global helper ──────────────────────────────────────────────── */
  /**
   * window.QC_ATTRIB.get(key, touch)
   *   key   — e.g. 'utm_source', 'campaign_id', 'landing_page'
   *   touch — 'lt' (last-touch, default) | 'ft' (first-touch)
   *
   * Returns stored string, or '' if not found.
   */
  window.QC_ATTRIB = {
    get: function (key, touch) {
      return getStored(key, touch || 'lt');
    }
  };

  /* ── DOM population ─────────────────────────────────────────────── */
  /**
   * Find every <input data-qc-lt="key"> and <input data-qc-ft="key">
   * in the page and fill their value from storage.
   * Safe to call multiple times (idempotent).
   */
  function populateFields() {
    try {
      var ltEls = document.querySelectorAll('[data-qc-lt]');
      for (var i = 0; i < ltEls.length; i++) {
        var el = ltEls[i];
        var v  = getStored(el.getAttribute('data-qc-lt'), 'lt');
        if (v) el.value = v;
      }
      var ftEls = document.querySelectorAll('[data-qc-ft]');
      for (var j = 0; j < ftEls.length; j++) {
        var el2 = ftEls[j];
        var v2  = getStored(el2.getAttribute('data-qc-ft'), 'ft');
        if (v2) el2.value = v2;
      }
    } catch (e) {}
  }

  /* Attach a capture-phase submit listener so values are refreshed
     just before the inline qc-quote-scripts listener collects FormData. */
  function attachSubmitListeners() {
    try {
      var forms = document.querySelectorAll('form');
      for (var i = 0; i < forms.length; i++) {
        (function (form) {
          form.addEventListener('submit', function () {
            populateFields();
          }, true);
        })(forms[i]);
      }
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      populateFields();
      attachSubmitListeners();
    });
  } else {
    populateFields();
    attachSubmitListeners();
  }
})();
